import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Trash2,
  Folder,
  Loader2,
  RefreshCw,
  Settings,
  AlertCircle,
  Clock,
} from "lucide-react";
import ExportButton from "./ExportButton";

const ContactGroupCard = ({
  group,
  onView,
  onDownload,
  onDelete,
  onAddToGroup,
  onRefreshGroup,
}) => {
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const timeoutRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getContactCountColor = (count) => {
    if (count === 0) return "text-blue-500";
    if (count < 100) return "text-blue-400";
    if (count < 1000) return "text-blue-600";
    return "text-purple-600";
  };

  const getContactCountBg = (count) => {
    if (count === 0) return "bg-blue-100";
    if (count < 100) return "bg-blue-100";
    if (count < 1000) return "bg-blue-100";
    return "bg-purple-100";
  };

  // Check if group is still being processed
  const isProcessing = !group.uploaded_at;
  const contactCount = group.number_of_contacts || 0;

  // Get card styling based on contact count
  const getCardStyling = (count) => {
    if (isProcessing) {
      return {
        background: showTimeoutWarning 
          ? "bg-gradient-to-br from-red-50 to-orange-50" 
          : "bg-gradient-to-br from-orange-50 to-amber-50",
        border: showTimeoutWarning ? "border-red-200" : "border-orange-200",
        iconBg: showTimeoutWarning 
          ? "from-red-500 to-orange-600" 
          : "from-orange-500 to-amber-600",
      };
    }
    if (count === 0) {
      return {
        background: "bg-gradient-to-br from-blue-50 to-blue-100",
        border: "border-blue-200",
        iconBg: "from-blue-400 to-blue-500",
      };
    }
    if (count < 100) {
      return {
        background: "bg-gradient-to-br from-blue-50 to-blue-60",
        border: "border-blue-200",
        iconBg: "from-blue-400 to-blue-600",
      };
    }
    if (count < 1000) {
      return {
        background: "bg-gradient-to-br from-red-50 to-red-50",
        border: "border-red-200",
        iconBg: "from-red-500 to-red-600",
      };
    }
    return {
      background: "bg-gradient-to-br from-purple-50 to-violet-50",
      border: "border-purple-200",
      iconBg: "from-purple-500 to-violet-600",
    };
  };

  // Timer for processing timeout
  useEffect(() => {
    if (isProcessing) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for 5 seconds
      timeoutRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
      }, 5000); // 5 seconds

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      // Not processing, reset the warning
      setShowTimeoutWarning(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isProcessing, group.uploaded_at]);

  const cardStyle = getCardStyling(contactCount);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className={`${cardStyle.background} rounded-xl sm:rounded-2xl shadow-lg border-2 ${cardStyle.border} p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden`}
    >
      {/* Decorative corner gradient */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl to-transparent rounded-bl-full from-white/20" />

      {/* Header Section */}
      <div className="flex relative z-10 justify-between items-start mb-6">
        <div className="flex flex-1 gap-4 items-center min-w-0">
          <div
            className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${cardStyle.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}
          >
            <Folder className="w-6 h-6 text-white sm:w-7 sm:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="mb-1 text-lg font-bold text-gray-900 truncate sm:text-xl"
              title={group.group_name}
            >
              {group.group_name}
            </h3>
            <div className="flex gap-3 items-center text-sm text-gray-600">
              <div className="flex gap-1 items-center">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(group.created_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Indicator - Shows processing status */}
        <div className="flex gap-2 items-center mt-1 mr-8">
          <div
            className={`w-3 h-3 rounded-full shadow-md border-2 border-white ${
              showTimeoutWarning
                ? "bg-red-500"
                : isProcessing
                ? "bg-yellow-500"
                : contactCount > 0
                ? "bg-red-500"
                : "bg-gray-400"
            }`}
          />
          <span className="text-xs font-medium text-gray-500">
            {showTimeoutWarning
              ? "Timeout"
              : isProcessing
              ? "Processing"
              : contactCount > 0
              ? "Active"
              : "Empty"}
          </span>
        </div>
      </div>

      {/* Contact Count Badge or Processing Status */}
      <div className="flex relative z-10 justify-center mb-6">
        {isProcessing ? (
          showTimeoutWarning ? (
            <div className="flex gap-3 items-center px-6 py-4 bg-gradient-to-r from-orange-50 to-orange-50 rounded-full border-2 border-orange-200 shadow-lg backdrop-blur-sm">
              < Clock className="w-6 h-6 text-orange-600" />
              <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">
                Processing
              </span>
            </div>
          ) : (
            <div className="flex gap-3 items-center px-6 py-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-full border-2 border-orange-200 shadow-lg backdrop-blur-sm">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6"
              >
                <Loader2 className="w-6 h-6 text-orange-600" />
              </motion.div>
              <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-700">
                Processing Contacts...
              </span>
            </div>
          )
        ) : (
          <div
            className={`px-6 py-3 rounded-full border shadow-sm ${getContactCountBg(
              contactCount
            )} border-white/50`}
          >
            <span
              className={`text-lg font-bold ${getContactCountColor(
                contactCount
              )}`}
            >
              {contactCount.toLocaleString()} Contacts
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex relative z-30 gap-3">
        {/* Add to Group Button */}
        {!isProcessing && !showTimeoutWarning && (
          <motion.button
            onClick={() => onAddToGroup(group)}
            className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-gray-800 rounded-xl border shadow-md backdrop-blur-sm transition-all duration-200 bg-white/70 hover:bg-white/90 hover:shadow-lg border-white/50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Settings className="w-4 h-4" />
            <span>Manage Group</span>
          </motion.button>
        )}

        {/* Export Button or Refresh/Retry Button */}
        {isProcessing ? (
          <motion.button
            onClick={() => {
              if (onRefreshGroup) {
                onRefreshGroup(group);
                // Reset the warning when manually refreshing
                setShowTimeoutWarning(false);
              }
            }}
            className={`flex flex-1 gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-white rounded-xl border shadow-md backdrop-blur-sm transition-all duration-200 hover:shadow-lg ${
              showTimeoutWarning
                ? "bg-gradient-to-r from-red-500 to-orange-600 border-red-500 hover:from-red-600 hover:to-orange-700"
                : "bg-gradient-to-r from-orange-500 to-amber-600 border-orange-500 hover:from-orange-600 hover:to-amber-700"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            title={showTimeoutWarning ? "Retry updating contacts" : "Check if processing is complete"}
          >
            {showTimeoutWarning ? (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Retry Update</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Check Status</span>
              </>
            )}
          </motion.button>
        ) : (
          <ExportButton
            groupSlug={group.contact_group_slug}
            groupName={group.group_name}
            contactCount={contactCount}
            variant="default"
            className="flex-1"
            disabled={contactCount === 0}
          />
        )}
      </div>

      {/* Delete Button - Floating */}
      <motion.button
        onClick={() => onDelete(group)}
        className="absolute top-4 right-4 z-40 p-2 text-gray-400 rounded-lg backdrop-blur-sm transition-all duration-200 hover:text-red-600 hover:bg-red-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        title="Delete Group"
      >
        <Trash2 className="w-4 h-4" />
      </motion.button>
    </motion.div>
  );
};

export default ContactGroupCard;